import { BaseLookup } from './base.lookup';

/** Shared base for job filter lookups that also have a URL/query key (slug). */
export class JobFilterLookup extends BaseLookup {
  key!: string;

  constructor(id: string, name: string, key: string) {
    super(id, name);
    this.key = key;
  }

  /** Alias so existing templates can keep using `.label`. */
  get label(): string {
    return this.name;
  }

  static override values: JobFilterLookup[] = [];

  static override fromId(id: string): JobFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  /** Resolve by URL key; accepts `owner+operator` / spaced variants. */
  static fromKey(key: string): JobFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

export function normalizeFilterKey(value: string): string {
  return value.trim().toLowerCase().replace(/[+\s]+/g, '-');
}

// ── CDL Class ──────────────────────────────────────────────────────────────

export class CdlClassFilterLookup extends JobFilterLookup {
  static readonly CLASS_A = new CdlClassFilterLookup('1', 'Class A', 'class-a');
  static readonly CLASS_B = new CdlClassFilterLookup('2', 'Class B', 'class-b');
  static readonly CLASS_C = new CdlClassFilterLookup('3', 'Class C', 'class-c');

  static override readonly values = [
    CdlClassFilterLookup.CLASS_A,
    CdlClassFilterLookup.CLASS_B,
    CdlClassFilterLookup.CLASS_C,
  ];

  static override fromId(id: string): CdlClassFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  static override fromKey(key: string): CdlClassFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

// ── Driver Type ────────────────────────────────────────────────────────────

export class DriverTypeFilterLookup extends JobFilterLookup {
  static readonly LEASE_PURCHASE = new DriverTypeFilterLookup('1', 'Lease Purchase', 'lease-purchase');
  static readonly COMPANY_DRIVER = new DriverTypeFilterLookup('2', 'Company Driver', 'company-driver');
  static readonly OWNER_OPERATOR = new DriverTypeFilterLookup('3', 'Owner Operator', 'owner-operator');

  static override readonly values = [
    DriverTypeFilterLookup.COMPANY_DRIVER,
    DriverTypeFilterLookup.LEASE_PURCHASE,
    DriverTypeFilterLookup.OWNER_OPERATOR,
  ];

  static override fromId(id: string): DriverTypeFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  static override fromKey(key: string): DriverTypeFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

// ── Freight Type ───────────────────────────────────────────────────────────

export class FreightTypeFilterLookup extends JobFilterLookup {
  static readonly DRY_VAN = new FreightTypeFilterLookup('1', 'Dry Van', 'dry-van');
  static readonly REFRIGERATED_REEFER = new FreightTypeFilterLookup('2', 'Refrigerated (Reefer)', 'refrigerated-reefer');
  static readonly FLATBED = new FreightTypeFilterLookup('3', 'Flatbed', 'flatbed');
  static readonly TANKER = new FreightTypeFilterLookup('4', 'Tanker', 'tanker');
  static readonly DOUBLES_TRIPLES = new FreightTypeFilterLookup('5', 'Doubles / Triples', 'doubles-triples');
  static readonly INTERMODAL = new FreightTypeFilterLookup('6', 'Intermodal', 'intermodal');
  static readonly HOUSEHOLD_GOODS = new FreightTypeFilterLookup('7', 'Household Goods', 'household-goods');
  static readonly HAZMAT = new FreightTypeFilterLookup('8', 'Hazmat', 'hazmat');
  static readonly SPECIALIZED = new FreightTypeFilterLookup('9', 'Specialized', 'specialized');
  static readonly AUTO_HAULER = new FreightTypeFilterLookup('10', 'Auto Hauler', 'auto-hauler');
  static readonly DOUBLE_PUPS = new FreightTypeFilterLookup('11', 'Double Pups', 'double-pups');
  static readonly DRY_BULK_PNEUMATIC = new FreightTypeFilterLookup('12', 'Dry Bulk & Pneumatic', 'dry-bulk-pneumatic');
  static readonly OTHER = new FreightTypeFilterLookup('13', 'Other', 'other');

  static override readonly values = [
    FreightTypeFilterLookup.DRY_VAN,
    FreightTypeFilterLookup.REFRIGERATED_REEFER,
    FreightTypeFilterLookup.FLATBED,
    FreightTypeFilterLookup.TANKER,
    FreightTypeFilterLookup.DOUBLES_TRIPLES,
    FreightTypeFilterLookup.INTERMODAL,
    FreightTypeFilterLookup.HOUSEHOLD_GOODS,
    FreightTypeFilterLookup.HAZMAT,
    FreightTypeFilterLookup.SPECIALIZED,
    FreightTypeFilterLookup.AUTO_HAULER,
    FreightTypeFilterLookup.DOUBLE_PUPS,
    FreightTypeFilterLookup.DRY_BULK_PNEUMATIC,
    FreightTypeFilterLookup.OTHER,
  ];

  static override fromId(id: string): FreightTypeFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  static override fromKey(key: string): FreightTypeFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

// ── Endorsement ────────────────────────────────────────────────────────────

export class EndorsementFilterLookup extends JobFilterLookup {
  static readonly HAZARDOUS_MATERIALS = new EndorsementFilterLookup('1', 'H - Hazardous Materials', 'hazardous-materials');
  static readonly TANKER_VEHICLES = new EndorsementFilterLookup('2', 'N - Tanker Vehicles', 'tanker-vehicles');
  static readonly DOUBLES_OR_TRIPLES = new EndorsementFilterLookup('3', 'T - Doubles or Triples', 'doubles-or-triples');
  static readonly PASSENGERS = new EndorsementFilterLookup('4', 'P - Passengers', 'passengers');
  static readonly SCHOOL_BUS = new EndorsementFilterLookup('5', 'S - School Bus', 'school-bus');
  static readonly COMBO_HAZMAT_TANK = new EndorsementFilterLookup('6', 'X - Combo Hazmat & Tank', 'combo-hazmat-tank');
  static readonly TWIC_CARD = new EndorsementFilterLookup('7', 'TWIC Card', 'twic-card');

  static override readonly values = [
    EndorsementFilterLookup.HAZARDOUS_MATERIALS,
    EndorsementFilterLookup.TANKER_VEHICLES,
    EndorsementFilterLookup.DOUBLES_OR_TRIPLES,
    EndorsementFilterLookup.PASSENGERS,
    EndorsementFilterLookup.SCHOOL_BUS,
    EndorsementFilterLookup.COMBO_HAZMAT_TANK,
    EndorsementFilterLookup.TWIC_CARD,
  ];

  static override fromId(id: string): EndorsementFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  static override fromKey(key: string): EndorsementFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

// ── Experience Type ────────────────────────────────────────────────────────

export class ExperienceTypeFilterLookup extends JobFilterLookup {
  static readonly NEED_CDL_TRAINING = new ExperienceTypeFilterLookup('1', 'Need CDL Training', 'need-cdl-training');
  static readonly IN_CDL_SCHOOL = new ExperienceTypeFilterLookup('2', 'In CDL School Now', 'in-cdl-school');
  static readonly CDL_GRAD_NO_EXPERIENCE = new ExperienceTypeFilterLookup('3', 'CDL Grad - No Experience Yet', 'cdl-grad-no-experience');
  static readonly ONE_TO_FIVE_MONTHS = new ExperienceTypeFilterLookup('4', '1-5 Months', '1-5-months');
  static readonly SIX_TO_ELEVEN_MONTHS = new ExperienceTypeFilterLookup('5', '6-11 Months', '6-11-months');
  static readonly TWELVE_TO_TWENTY_THREE_MONTHS = new ExperienceTypeFilterLookup('6', '12-23 Months', '12-23-months');
  static readonly TWO_YEARS = new ExperienceTypeFilterLookup('7', '2 Years', '2-years');
  static readonly THREE_YEARS = new ExperienceTypeFilterLookup('8', '3 Years', '3-years');
  static readonly FOUR_YEARS = new ExperienceTypeFilterLookup('9', '4 Years', '4-years');
  static readonly FIVE_PLUS_YEARS = new ExperienceTypeFilterLookup('10', '5+ Years', '5-plus-years');

  static override readonly values = [
    ExperienceTypeFilterLookup.NEED_CDL_TRAINING,
    ExperienceTypeFilterLookup.IN_CDL_SCHOOL,
    ExperienceTypeFilterLookup.CDL_GRAD_NO_EXPERIENCE,
    ExperienceTypeFilterLookup.ONE_TO_FIVE_MONTHS,
    ExperienceTypeFilterLookup.SIX_TO_ELEVEN_MONTHS,
    ExperienceTypeFilterLookup.TWELVE_TO_TWENTY_THREE_MONTHS,
    ExperienceTypeFilterLookup.TWO_YEARS,
    ExperienceTypeFilterLookup.THREE_YEARS,
    ExperienceTypeFilterLookup.FOUR_YEARS,
    ExperienceTypeFilterLookup.FIVE_PLUS_YEARS,
  ];

  static override fromId(id: string): ExperienceTypeFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  static override fromKey(key: string): ExperienceTypeFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

// ── Hiring State ───────────────────────────────────────────────────────────

export class HiringStateFilterLookup extends JobFilterLookup {
  static readonly ALABAMA = new HiringStateFilterLookup('1', 'Alabama', 'alabama');
  static readonly ARIZONA = new HiringStateFilterLookup('4', 'Arizona', 'arizona');
  static readonly ARKANSAS = new HiringStateFilterLookup('5', 'Arkansas', 'arkansas');
  static readonly CALIFORNIA = new HiringStateFilterLookup('6', 'California', 'california');
  static readonly COLORADO = new HiringStateFilterLookup('7', 'Colorado', 'colorado');
  static readonly CONNECTICUT = new HiringStateFilterLookup('8', 'Connecticut', 'connecticut');
  static readonly DELAWARE = new HiringStateFilterLookup('9', 'Delaware', 'delaware');
  static readonly DISTRICT_OF_COLUMBIA = new HiringStateFilterLookup('10', 'District of Columbia', 'district-of-columbia');
  static readonly FLORIDA = new HiringStateFilterLookup('12', 'Florida', 'florida');
  static readonly GEORGIA = new HiringStateFilterLookup('13', 'Georgia', 'georgia');
  static readonly IDAHO = new HiringStateFilterLookup('16', 'Idaho', 'idaho');
  static readonly ILLINOIS = new HiringStateFilterLookup('17', 'Illinois', 'illinois');
  static readonly INDIANA = new HiringStateFilterLookup('18', 'Indiana', 'indiana');
  static readonly IOWA = new HiringStateFilterLookup('19', 'Iowa', 'iowa');
  static readonly KANSAS = new HiringStateFilterLookup('20', 'Kansas', 'kansas');
  static readonly KENTUCKY = new HiringStateFilterLookup('21', 'Kentucky', 'kentucky');
  static readonly LOUISIANA = new HiringStateFilterLookup('22', 'Louisiana', 'louisiana');
  static readonly MAINE = new HiringStateFilterLookup('23', 'Maine', 'maine');
  static readonly MARYLAND = new HiringStateFilterLookup('25', 'Maryland', 'maryland');
  static readonly MASSACHUSETTS = new HiringStateFilterLookup('26', 'Massachusetts', 'massachusetts');
  static readonly MICHIGAN = new HiringStateFilterLookup('27', 'Michigan', 'michigan');
  static readonly MINNESOTA = new HiringStateFilterLookup('28', 'Minnesota', 'minnesota');
  static readonly MISSISSIPPI = new HiringStateFilterLookup('29', 'Mississippi', 'mississippi');
  static readonly MISSOURI = new HiringStateFilterLookup('30', 'Missouri', 'missouri');
  static readonly MONTANA = new HiringStateFilterLookup('31', 'Montana', 'montana');
  static readonly NEBRASKA = new HiringStateFilterLookup('32', 'Nebraska', 'nebraska');
  static readonly NEVADA = new HiringStateFilterLookup('33', 'Nevada', 'nevada');
  static readonly NEW_HAMPSHIRE = new HiringStateFilterLookup('34', 'New Hampshire', 'new-hampshire');
  static readonly NEW_JERSEY = new HiringStateFilterLookup('35', 'New Jersey', 'new-jersey');
  static readonly NEW_MEXICO = new HiringStateFilterLookup('36', 'New Mexico', 'new-mexico');
  static readonly NEW_YORK = new HiringStateFilterLookup('37', 'New York', 'new-york');
  static readonly NORTH_CAROLINA = new HiringStateFilterLookup('38', 'North Carolina', 'north-carolina');
  static readonly NORTH_DAKOTA = new HiringStateFilterLookup('39', 'North Dakota', 'north-dakota');
  static readonly OHIO = new HiringStateFilterLookup('41', 'Ohio', 'ohio');
  static readonly OKLAHOMA = new HiringStateFilterLookup('42', 'Oklahoma', 'oklahoma');
  static readonly OREGON = new HiringStateFilterLookup('43', 'Oregon', 'oregon');
  static readonly PENNSYLVANIA = new HiringStateFilterLookup('45', 'Pennsylvania', 'pennsylvania');
  static readonly RHODE_ISLAND = new HiringStateFilterLookup('47', 'Rhode Island', 'rhode-island');
  static readonly SOUTH_CAROLINA = new HiringStateFilterLookup('48', 'South Carolina', 'south-carolina');
  static readonly SOUTH_DAKOTA = new HiringStateFilterLookup('49', 'South Dakota', 'south-dakota');
  static readonly TENNESSEE = new HiringStateFilterLookup('50', 'Tennessee', 'tennessee');
  static readonly TEXAS = new HiringStateFilterLookup('51', 'Texas', 'texas');
  static readonly UTAH = new HiringStateFilterLookup('52', 'Utah', 'utah');
  static readonly VERMONT = new HiringStateFilterLookup('53', 'Vermont', 'vermont');
  static readonly VIRGINIA = new HiringStateFilterLookup('55', 'Virginia', 'virginia');
  static readonly WASHINGTON = new HiringStateFilterLookup('56', 'Washington', 'washington');
  static readonly WEST_VIRGINIA = new HiringStateFilterLookup('57', 'West Virginia', 'west-virginia');
  static readonly WISCONSIN = new HiringStateFilterLookup('58', 'Wisconsin', 'wisconsin');
  static readonly WYOMING = new HiringStateFilterLookup('59', 'Wyoming', 'wyoming');

  static override readonly values = [
    HiringStateFilterLookup.ALABAMA,
    HiringStateFilterLookup.ARIZONA,
    HiringStateFilterLookup.ARKANSAS,
    HiringStateFilterLookup.CALIFORNIA,
    HiringStateFilterLookup.COLORADO,
    HiringStateFilterLookup.CONNECTICUT,
    HiringStateFilterLookup.DELAWARE,
    HiringStateFilterLookup.DISTRICT_OF_COLUMBIA,
    HiringStateFilterLookup.FLORIDA,
    HiringStateFilterLookup.GEORGIA,
    HiringStateFilterLookup.IDAHO,
    HiringStateFilterLookup.ILLINOIS,
    HiringStateFilterLookup.INDIANA,
    HiringStateFilterLookup.IOWA,
    HiringStateFilterLookup.KANSAS,
    HiringStateFilterLookup.KENTUCKY,
    HiringStateFilterLookup.LOUISIANA,
    HiringStateFilterLookup.MAINE,
    HiringStateFilterLookup.MARYLAND,
    HiringStateFilterLookup.MASSACHUSETTS,
    HiringStateFilterLookup.MICHIGAN,
    HiringStateFilterLookup.MINNESOTA,
    HiringStateFilterLookup.MISSISSIPPI,
    HiringStateFilterLookup.MISSOURI,
    HiringStateFilterLookup.MONTANA,
    HiringStateFilterLookup.NEBRASKA,
    HiringStateFilterLookup.NEVADA,
    HiringStateFilterLookup.NEW_HAMPSHIRE,
    HiringStateFilterLookup.NEW_JERSEY,
    HiringStateFilterLookup.NEW_MEXICO,
    HiringStateFilterLookup.NEW_YORK,
    HiringStateFilterLookup.NORTH_CAROLINA,
    HiringStateFilterLookup.NORTH_DAKOTA,
    HiringStateFilterLookup.OHIO,
    HiringStateFilterLookup.OKLAHOMA,
    HiringStateFilterLookup.OREGON,
    HiringStateFilterLookup.PENNSYLVANIA,
    HiringStateFilterLookup.RHODE_ISLAND,
    HiringStateFilterLookup.SOUTH_CAROLINA,
    HiringStateFilterLookup.SOUTH_DAKOTA,
    HiringStateFilterLookup.TENNESSEE,
    HiringStateFilterLookup.TEXAS,
    HiringStateFilterLookup.UTAH,
    HiringStateFilterLookup.VERMONT,
    HiringStateFilterLookup.VIRGINIA,
    HiringStateFilterLookup.WASHINGTON,
    HiringStateFilterLookup.WEST_VIRGINIA,
    HiringStateFilterLookup.WISCONSIN,
    HiringStateFilterLookup.WYOMING,
  ];

  static override fromId(id: string): HiringStateFilterLookup | undefined {
    return this.values.find((v) => v.id === id);
  }

  static override fromKey(key: string): HiringStateFilterLookup | undefined {
    const normalized = normalizeFilterKey(key);
    return this.values.find((v) => v.key === normalized);
  }
}

// ── Browse / search dimension registry ─────────────────────────────────────

export const JOB_FILTER_DIMENSIONS = {
  'state': {
    param: 'hiringState',
    values: HiringStateFilterLookup.values,
  },
  'driver-type': {
    param: 'driverType',
    values: DriverTypeFilterLookup.values,
  },
  'cdl-class': {
    param: 'cdlClass',
    values: CdlClassFilterLookup.values,
  },
  'freight-type': {
    param: 'freightType',
    values: FreightTypeFilterLookup.values,
  },
  'endorsement': {
    param: 'endorsement',
    values: EndorsementFilterLookup.values,
  },
  'experience': {
    param: 'experienceType',
    values: ExperienceTypeFilterLookup.values,
  },
} as const;
